import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  EMPTY,
  Observable,
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  first,
  map,
  shareReplay,
  tap,
} from 'rxjs';
import { CoreModule } from 'src/app/core/core.module';
import { SortChangeEvent } from 'src/app/core/directives/sortable-header.directive';
import { ResourceTypes } from 'src/app/core/enums/resource-types.enum';
import { UserCompanyPermissions } from 'src/app/core/enums/user-company-permissions.enum';
import { PagedList } from 'src/app/core/models/paged-list.interface';
import { RequestModel } from 'src/app/core/models/request-model.interface';
import { CompanyShipment } from 'src/app/core/models/sales/company-shipment.interface';
import { CompanyShipmentDetails } from 'src/app/core/models/sales/company-shipment-details.interface';
import {
  ShipmentStatus,
  getShipmentStatusBadgeKey,
  getShipmentStatusLabelKey,
} from 'src/app/core/models/sales/shipment-status.type';
import { PermissionService } from 'src/app/core/services/permission.service';
import { ShipmentService } from 'src/app/core/services/shipment.service';
import { CreateShipmentModalComponent } from './create-shipment-modal/create-shipment-modal.component';

interface ShipmentRowView {
  id: string;
  number: string;
  customerLabel: string;
  orderLabel: string;
  shipmentDate: string;
  status: ShipmentStatus;
  statusLabelKey: string;
  statusBadgeKey: 'warning' | 'success';
  canMarkShipped: boolean;
}

@Component({
  selector: 'app-shipments',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslateModule, CoreModule, CreateShipmentModalComponent],
  templateUrl: './shipments.component.html',
  styleUrl: './shipments.component.scss',
})
export class ShipmentsComponent {
  public readonly emptyListParam: Record<string, string> = {
    uk: 'відвантажень',
    en: 'shipments',
  };

  public companyId = '';
  public isLoading = false;
  public isCreateModalOpen = false;
  public errorMessageKey = '';
  public searchTerm = '';
  public currentLang = 'en';

  public isMarkShippedModalOpen = false;
  public isMarkShippedInFlight = false;
  public markShippedTarget: ShipmentRowView | null = null;
  public markShippedErrorKey = '';

  // Map structured backend error codes to i18n keys for the row-action banner.
  // Keep in sync with TaskManager.Services.ShipmentErrorCodes.
  private readonly shipmentErrorCodeKeyMap: Readonly<Record<string, string>> = {
    SHIPMENT_INVALID_STATUS: 'COMPONENTS.SHIPMENTS.ERRORS.SHIPMENT_INVALID_STATUS',
    SHIPMENT_TERMINAL: 'COMPONENTS.SHIPMENTS.ERRORS.SHIPMENT_TERMINAL',
    INSUFFICIENT_STOCK: 'COMPONENTS.SHIPMENTS.ERRORS.INSUFFICIENT_STOCK',
    ORDER_TERMINAL: 'COMPONENTS.SHIPMENTS.ERRORS.ORDER_TERMINAL',
    ORDER_INVALID_STATUS: 'COMPONENTS.SHIPMENTS.ERRORS.ORDER_INVALID_STATUS',
  };

  public shipments$: Observable<PagedList<ShipmentRowView>> = EMPTY;

  private readonly shipmentService = inject(ShipmentService);
  private readonly permissionService = inject(PermissionService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);

  public readonly canCreateShipment$ = this.permissionService.userPermissions$.pipe(
    map((permissions) =>
      this.permissionService.hasPermission(
        permissions,
        ResourceTypes.Company,
        UserCompanyPermissions.Edit,
      ),
    ),
    shareReplay(1),
  );

  private readonly searchChanges$ = new Subject<string>();
  private request: RequestModel = {
    page: 1,
    pageSize: 10,
    filters: [],
    sortColumn: 'shipmentDate',
    sortOrder: 'desc',
  };

  constructor() {
    const route = inject(ActivatedRoute);
    route.parent?.parent?.params
      .pipe(
        first(),
        tap((params) => {
          this.companyId = params['id'];
          this.currentLang = this.translateService.currentLang === 'uk' ? 'uk' : 'en';
          this.isLoading = true;
          this.loadShipments();
        }),
      )
      .subscribe();

    this.searchChanges$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((value) => {
          this.request.page = 1;
          this.request.searchTerm = value.trim() || undefined;
          this.isLoading = true;
          this.loadShipments();
        }),
      )
      .subscribe();
  }

  public openCreateModal(): void {
    this.isCreateModalOpen = true;
  }

  public closeCreateModal(): void {
    this.isCreateModalOpen = false;
  }

  public onShipmentCreated(shipment: CompanyShipmentDetails): void {
    this.closeCreateModal();
    void this.router.navigate(['/companies', this.companyId, 'crm', 'shipments', shipment.id]);
  }

  public retry(): void {
    this.isLoading = true;
    this.loadShipments();
  }

  public onPageChange(page: number): void {
    this.request.page = page;
    this.loadShipments();
  }

  public onChangePageSize(size: number): void {
    this.request.pageSize = size;
    this.request.page = 1;
    this.loadShipments();
  }

  public onSearchChange(value: string): void {
    this.searchTerm = value;
    this.searchChanges$.next(value);
  }

  public onSortChange(event: SortChangeEvent): void {
    this.request.sortColumn = event.column;
    this.request.sortOrder = event.order;
    this.request.page = 1;
    this.isLoading = true;
    this.loadShipments();
  }

  public get activeSortColumn(): string {
    return this.request.sortColumn ?? '';
  }

  public get activeSortOrder(): 'asc' | 'desc' {
    return this.request.sortOrder ?? 'asc';
  }

  private loadShipments(): void {
    if (!this.companyId) {
      return;
    }

    this.errorMessageKey = '';
    this.shipments$ = this.shipmentService.getShipmentsPaged(this.companyId, this.request).pipe(
      map((page) => ({
        ...page,
        items: page.items.map((shipment) => this.mapShipmentToRow(shipment)),
      })),
      tap(() => {
        this.isLoading = false;
      }),
      catchError(() => {
        this.errorMessageKey = 'COMPONENTS.SHIPMENTS.ERRORS.LOAD_FAILED';
        this.isLoading = false;
        return EMPTY;
      }),
      shareReplay(1),
    );
  }

  public openMarkShippedModal(row: ShipmentRowView): void {
    if (!row.canMarkShipped) {
      return;
    }
    this.markShippedTarget = row;
    this.markShippedErrorKey = '';
    this.isMarkShippedModalOpen = true;
  }

  public closeMarkShippedModal(): void {
    if (this.isMarkShippedInFlight) {
      return;
    }
    this.isMarkShippedModalOpen = false;
    this.markShippedTarget = null;
    this.markShippedErrorKey = '';
  }

  public confirmMarkShipped(): void {
    const target = this.markShippedTarget;
    if (!target || this.isMarkShippedInFlight || !this.companyId) {
      return;
    }
    this.isMarkShippedInFlight = true;
    this.markShippedErrorKey = '';

    this.shipmentService
      .markShipped(this.companyId, target.id)
      .pipe(
        tap(() => {
          this.isMarkShippedModalOpen = false;
          this.markShippedTarget = null;
          this.isLoading = true;
          this.loadShipments();
        }),
        catchError((error: HttpErrorResponse) => {
          this.markShippedErrorKey = this.mapShipmentErrorKey(
            error,
            'COMPONENTS.SHIPMENTS.ERRORS.UPDATE_FAILED',
          );
          return EMPTY;
        }),
        finalize(() => {
          this.isMarkShippedInFlight = false;
        }),
      )
      .subscribe();
  }

  private mapShipmentToRow(shipment: CompanyShipment): ShipmentRowView {
    const status: ShipmentStatus = shipment.status === 'Shipped' ? 'Shipped' : 'Packed';
    return {
      id: shipment.id,
      number: shipment.number || '—',
      customerLabel: shipment.customerName || (shipment.customerId ? this.shortId(shipment.customerId) : '—'),
      orderLabel: shipment.salesOrderNumber || (shipment.salesOrderId ? this.shortId(shipment.salesOrderId) : '—'),
      shipmentDate: shipment.shipmentDate ?? '',
      status,
      statusLabelKey: getShipmentStatusLabelKey(status),
      statusBadgeKey: getShipmentStatusBadgeKey(status),
      canMarkShipped: status === 'Packed',
    };
  }

  private mapShipmentErrorKey(error: HttpErrorResponse, fallback: string): string {
    if (error.status === 400 || error.status === 409 || error.status === 422) {
      const code = (error.error as { code?: string } | null)?.code;
      if (code && this.shipmentErrorCodeKeyMap[code]) {
        return this.shipmentErrorCodeKeyMap[code];
      }
    }
    return fallback;
  }

  private shortId(value: string): string {
    return value.length > 8 ? `${value.slice(0, 8)}…` : value;
  }
}
